import { ContentType } from "../../constants";
import { AutoTranslateLanguage } from "../../types/StoryblokTypes";
import { getContentTypeFromPath } from "./contentType";

/**
 * Returns the language the user is currently viewing content in.
 *
 * On article pages with auto-translate active the translated language is used;
 * in all other cases the user's UI language preference is returned.
 * 
 * @param pathname the current pathname, used to determine the content type
 * @param languagePreference the user's UI language preference, used as a fallback
 * @param autoTranslateEnabled whether auto-translate is active for the user
 * @param autoTranslateLanguage the user's auto-translate language, used when active
 * @returns the content language to be used for analytics tracking
 * 
 * @example
 * getContentLanguage("/news/some-article", "nl", true, "vi") // "vi" (auto-translate language)
 * getContentLanguage("/en/news/some-article", "en", false, "de") // "en" (language preference, auto-translate not active)
 * getContentLanguage("/people-finder", "nl", true, "vi")   // "nl" (language preference, non-article page)
 */
export const getContentLanguage = (
  pathname: string,
  languagePreference: "nl" | "en",
  autoTranslateEnabled: boolean,
  autoTranslateLanguage: AutoTranslateLanguage | null,
): AutoTranslateLanguage => {
  const contentType = getContentTypeFromPath(pathname);

  if (
    contentType === ContentType.article &&
    autoTranslateEnabled &&
    autoTranslateLanguage !== null
  ) {
    return autoTranslateLanguage;
  }

  return languagePreference;
};
